import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Col, Row, Skeleton, Tag } from 'antd';
import {
  CheckCircleFilled,
  FileExcelOutlined,
  NotificationOutlined,
  ReloadOutlined,
  RightOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { isVisibleFor, NAV_GROUPS } from 'layouts/nav-config';
import { PENDING_SOURCES, sourceAllowedFor, usePendingCounts } from 'layouts/pending-counts';
import { toneColors } from 'theme/tokens';
import styles from './styles.module.scss';

interface Stat {
  key: string;
  label: string;
  value?: number;
  hint: string;
  to: string;
  tone: keyof typeof toneColors;
}

const readTotal = (res: any): number | undefined => {
  const body = res?.data ?? res;
  const total = body?.metadata?.total ?? body?.meta?.total ?? body?.total;
  return typeof total === 'number' ? total : undefined;
};

const greeting = () => {
  const hour = dayjs().hour();
  if (hour < 11) return 'Chào buổi sáng';
  if (hour < 14) return 'Chào buổi trưa';
  if (hour < 18) return 'Chào buổi chiều';
  return 'Chào buổi tối';
};

const formatNumber = (value?: number) =>
  typeof value === 'number' ? value.toLocaleString('vi-VN') : '—';

/** Số liệu nhanh. Nguồn chỉ-ADMIN không gọi khi là MOD (tránh 403). */
function useQuickStats(isAdmin: boolean, loaded: boolean) {
  const [stats, setStats] = useState<Stat[] | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!loaded) return;
    let cancelled = false;
    const today = dayjs().format('YYYY-MM-DD');
    const month = dayjs().format('YYYY-MM');
    const defs: (Stat & { url: string; params: Record<string, any>; adminOnly?: boolean })[] = [
      {
        key: 'users',
        label: 'Thành viên',
        hint: 'Tổng tài khoản trên hệ thống',
        to: '/users-management',
        tone: 'accent',
        url: API.users,
        params: { page: 1, limit: 1 },
        adminOnly: true,
      },
      {
        key: 'present',
        label: 'Lượt chấm công hôm nay',
        hint: `Ngày ${dayjs().format('DD/MM')}`,
        to: `/attendance-management?date=${today}`,
        tone: 'purple',
        url: API.attendanceList,
        params: { date: today, page: 1, limit: 1 },
        adminOnly: true,
      },
      {
        key: 'late',
        label: 'Đi trễ hôm nay',
        hint: 'Bấm để xem danh sách',
        to: `/attendance-management?date=${today}&status=late`,
        tone: 'warning',
        url: API.attendanceList,
        params: { date: today, status: 'late', page: 1, limit: 1 },
        adminOnly: true,
      },
      {
        key: 'draft-payslips',
        label: 'Phiếu lương nháp',
        hint: `Tháng ${dayjs().format('MM/YYYY')} — chưa chốt`,
        to: `/payslips-management?month=${month}`,
        tone: 'info',
        url: API.payslips,
        params: { month, status: 'DRAFT', page: 1, limit: 1 },
      },
    ];
    const visible = defs.filter((def) => !def.adminOnly || isAdmin);
    setStats(null);
    Promise.all(
      visible.map((def) =>
        AxiosUtils.get(def.url, def.params)
          .then((res) => readTotal(res))
          .catch(() => undefined)
      )
    ).then((values) => {
      if (cancelled) return;
      setStats(
        visible.map(({ url, params, adminOnly, ...stat }, index) => ({
          ...stat,
          value: values[index],
        }))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, loaded, reloadKey]);

  return { stats, reload: () => setReloadKey((key) => key + 1) };
}

const HomePage = () => {
  const {
    displayName,
    isAdmin,
    loaded,
    canImportData,
    canViewOnlineReward,
    canUseEquipmentQuote,
    audience,
  } = useCurrentRoles();
  const pending = usePendingCounts();
  const { stats, reload } = useQuickStats(isAdmin, loaded);
  // Chỉ thẻ của nguồn mà người xem này gọi (vd. nguồn Studio chỉ cho Admin).
  const sources = PENDING_SOURCES.filter((source) => sourceAllowedFor(source, audience));

  const totalPending = Object.values(pending.bySource).reduce((sum, count) => sum + count, 0);
  // Nguồn gọi lỗi không có key trong bySource (thẻ hiện "Không tải được") → tổng 0 lúc đó chưa chắc
  // là hết việc; đừng báo "Không còn việc nào" trái với các thẻ.
  const failedSources = sources.filter(
    (source) => pending.bySource[source.key] === undefined
  ).length;

  const quickActions = [
    {
      to: '/notification-management',
      icon: <NotificationOutlined />,
      label: 'Gửi thông báo',
      adminOnly: true,
    },
    {
      to: '/users-management?action=create',
      icon: <UserAddOutlined />,
      label: 'Thêm thành viên',
      adminOnly: true,
    },
    {
      // Hiện theo quyền nhập dữ liệu (ADMIN, hoặc MOD được Admin cấp), không theo vai trò.
      to: '/data-import',
      icon: <FileExcelOutlined />,
      label: 'Nhập file TikTok',
      allowed: canImportData,
    },
    { to: '/payslips-management', icon: <WalletOutlined />, label: 'Phiếu lương tháng' },
    { to: '/pk-management', icon: <ThunderboltOutlined />, label: 'PK Đại chiến' },
  ].filter((action) => (!action.adminOnly || isAdmin) && action.allowed !== false);

  return (
    <div className={styles.home}>
      <section className={styles.hero}>
        <div>
          <p className={styles.date}>{dayjs().format('dddd, DD/MM/YYYY')}</p>
          <h1>
            {greeting()}
            {displayName ? `, ${displayName}` : ''}
          </h1>
          <p className={styles.heroSub}>
            {!pending.loaded
              ? 'Đang kiểm tra việc cần xử lý…'
              : totalPending > 0
              ? `Có ${totalPending} việc đang chờ bạn duyệt.`
              : failedSources === sources.length
              ? 'Chưa tải được việc cần xử lý — bấm nút “Làm mới” cạnh mục “Cần xử lý” để thử lại.'
              : failedSources > 0
              ? 'Chưa thấy việc chờ duyệt, nhưng còn mục chưa tải được.'
              : 'Không còn việc nào chờ duyệt. Tuyệt vời!'}
          </p>
        </div>
        <div className={styles.heroActions}>
          {quickActions.map((action) => (
            <Link key={action.label} to={action.to}>
              <Button icon={action.icon}>{action.label}</Button>
            </Link>
          ))}
        </div>
      </section>

      <div className={styles.sectionHead}>
        <h2>Cần xử lý</h2>
        {/* Hiện chữ "Làm mới" (câu hero chỉ tới nút này; trên điện thoại không có tooltip).
            aria-label: không có thì tên trợ năng là "reload Làm mới" (nhãn tiếng Anh của icon). */}
        <Button
          type="text"
          size="small"
          icon={<ReloadOutlined />}
          aria-label="Làm mới"
          onClick={() => {
            pending.refresh();
            reload();
          }}
        >
          Làm mới
        </Button>
      </div>
      <div className={styles.queueGrid}>
        {sources.map((source) => {
          const count = pending.bySource[source.key];
          const done = pending.loaded && count === 0;
          return (
            <Link key={source.key} to={source.to} className={styles.queueCard}>
              <div className={styles.queueTop}>
                <span className={styles.queueLabel}>{source.label}</span>
                <RightOutlined className={styles.queueArrow} />
              </div>
              {!pending.loaded ? (
                <Skeleton.Input active size="small" />
              ) : count === undefined ? (
                <span className={styles.queueMuted}>Không tải được</span>
              ) : done ? (
                <span className={styles.queueDone}>
                  <CheckCircleFilled /> Đã xử lý hết
                </span>
              ) : (
                <span className={styles.queueCount}>{formatNumber(count)}</span>
              )}
            </Link>
          );
        })}
      </div>

      <div className={styles.sectionHead}>
        <h2>Số liệu nhanh</h2>
      </div>
      <Row gutter={[16, 16]}>
        {(stats || Array.from({ length: isAdmin ? 4 : 1 }, () => null)).map((stat, index) => (
          <Col key={stat?.key ?? index} xs={24} sm={12} lg={6}>
            {stat ? (
              <Link to={stat.to} className={styles.statCard}>
                <span
                  className={styles.statDot}
                  style={{
                    background: toneColors[stat.tone].soft,
                    color: toneColors[stat.tone].ink,
                  }}
                />
                <span className={styles.statLabel}>{stat.label}</span>
                <span className={styles.statValue}>{formatNumber(stat.value)}</span>
                <span className={styles.statHint}>{stat.hint}</span>
              </Link>
            ) : (
              <Card>
                <Skeleton active paragraph={{ rows: 1 }} />
              </Card>
            )}
          </Col>
        ))}
      </Row>

      <div className={styles.sectionHead}>
        <h2>Tất cả chức năng</h2>
        <span className={styles.sectionHint}>
          Mẹo: bấm <kbd>Ctrl</kbd> + <kbd>K</kbd> ở bất kỳ trang nào để tìm nhanh.
        </span>
      </div>
      <Row gutter={[16, 16]}>
        {NAV_GROUPS.map((group) => {
          const items = group.items.filter((item) =>
            isVisibleFor(
              item,
              isAdmin,
              {
                dataImport: canImportData,
                onlineReward: canViewOnlineReward,
                equipmentQuote: canUseEquipmentQuote,
              },
              audience
            )
          );
          if (!items.length) return null;
          const tone = toneColors[group.tone];
          return (
            <Col key={group.key} xs={24} md={12} xl={8}>
              <div className={styles.groupCard}>
                <div className={styles.groupHead}>
                  <span
                    className={styles.groupIcon}
                    style={{ background: tone.soft, color: tone.ink }}
                  >
                    {group.icon}
                  </span>
                  <div>
                    <h3>{group.label}</h3>
                    <p>{group.description}</p>
                  </div>
                </div>
                <div className={styles.groupLinks}>
                  {items.map((item) => (
                    <Link key={item.key} to={`/${item.key}`} className={styles.groupLink}>
                      <span>{item.label}</span>
                      {pending.byKey[item.key] ? (
                        <Tag color="magenta" bordered={false}>
                          {pending.byKey[item.key]}
                        </Tag>
                      ) : null}
                    </Link>
                  ))}
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default HomePage;
